import React, { useMemo, useState } from 'react';
import { Box, Button, Flex, Spinner, Text, Textarea } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowCounterClockwise, Buildings, ClockCounterClockwise, Prohibit } from 'phosphor-react';
import { toast } from 'sonner';
import { identificacaoDaEmpresa } from '../../hooks/useCentralPermissoes';
import { useAuth } from '../../providers/auth';
import { quandoFoi } from './acessoApi';
import {
  detalheDoMotivo,
  IEmpresaSituacao,
  MotivoInativa,
  MOTIVOS,
  situacaoApi,
  situacaoDaEmpresa,
} from './situacaoEmpresa';
import { AZUL, Busca, Cartao, Etiqueta, Eyebrow, MONO, Pilula, TEXTO, TEXTO_SUAVE } from './ui';

const POR_PAGINA = 40;
const semRota = (e: any) => e?.response?.status === 404 || e?.response?.status === 405;

export function useSituacaoDasEmpresas() {
  const consulta = useQuery({
    queryKey: ['central', 'situacao-empresas'],
    queryFn: situacaoApi.todas,
    staleTime: 5 * 60 * 1000,
  });
  const resumo = useMemo(() => {
    const empresas = consulta.data?.empresas ?? [];
    const inativas: (IEmpresaSituacao & { motivo: MotivoInativa })[] = [];
    let ativas = 0;
    empresas.forEach(e => {
      const s = situacaoDaEmpresa(e);
      if (s.ativa) ativas += 1;
      else inativas.push({ ...e, motivo: s.motivo ?? 'sem-situacao' });
    });
    const porMotivo: Partial<Record<MotivoInativa, number>> = {};
    inativas.forEach(e => {
      porMotivo[e.motivo] = (porMotivo[e.motivo] ?? 0) + 1;
    });
    return { ativas, inativas, porMotivo, total: empresas.length };
  }, [consulta.data]);
  return {
    ...resumo,
    carregando: consulta.isLoading,
    erro: consulta.isError,
    rotaPropria: consulta.data?.rotaPropria,
  };
}

/** Cartão da coluna da esquerda: quantas ativas e inativas, com a regra do Orquestrador. */
export function ResumoDaSituacao({
  aberto,
  onVerInativas,
}: {
  aberto: boolean;
  onVerInativas: () => void;
}) {
  const { ativas, inativas, carregando, erro } = useSituacaoDasEmpresas();
  return (
    <Box
      p="12px 14px"
      borderRadius="16px"
      bg="rgba(255,255,255,.75)"
      boxShadow={aberto ? `inset 0 0 0 1px ${AZUL}55` : 'inset 0 0 0 1px rgba(15,23,42,.07)'}
    >
      <Eyebrow>SITUAÇÃO DAS EMPRESAS</Eyebrow>
      {carregando && <Spinner size="sm" color={AZUL} mt="8px" />}
      {erro && (
        <Text fontSize="12.5px" color="#991B1B" mt="6px">
          Não foi possível contar as empresas.
        </Text>
      )}
      {!carregando && !erro && (
        <>
          <Flex gap="10px" mt="8px">
            <Box flex={1} p="8px 10px" borderRadius="12px" bg="rgba(220,252,231,.8)">
              <Text fontFamily={MONO} fontSize="20px" fontWeight={600} color="#166534">
                {ativas.toLocaleString('pt-BR')}
              </Text>
              <Text fontSize="12px" color="#166534">
                ativas
              </Text>
            </Box>
            <Box
              as="button"
              type="button"
              flex={1}
              p="8px 10px"
              borderRadius="12px"
              textAlign="left"
              bg={aberto ? '#F1F5F9' : 'rgba(241,245,249,.7)'}
              boxShadow={aberto ? 'inset 0 0 0 1px #94A3B8' : undefined}
              _hover={{ bg: '#F1F5F9' }}
              onClick={onVerInativas}
            >
              <Text fontFamily={MONO} fontSize="20px" fontWeight={600} color="#475569">
                {inativas.length.toLocaleString('pt-BR')}
              </Text>
              <Text fontSize="12px" color="#475569">
                inativas · ver
              </Text>
            </Box>
          </Flex>
          <Text fontSize="11.5px" color={TEXTO_SUAVE} mt="8px">
            Mesma regra do Orquestrador: situação “Ativa” e hoje dentro das datas de cliente. Nas
            outras abas só aparecem as ativas.
          </Text>
        </>
      )}
    </Box>
  );
}

function Reativar({
  empresa,
  onFeito,
}: {
  empresa: IEmpresaSituacao & { motivo: MotivoInativa };
  onFeito: () => void;
}) {
  const queryClient = useQueryClient();
  const [motivo, setMotivo] = useState('');
  const limpo = motivo.trim();
  let muda = `A situação no cadastro passa de “${empresa.ativa || 'vazia'}” para “Ativa”.`;
  if (empresa.motivo === 'saiu')
    muda = 'A data de fim como cliente é apagada: ela volta a ser cliente hoje.';
  if (empresa.motivo === 'ainda-nao-cliente')
    muda = 'A data de início como cliente passa a ser hoje.';
  const reativar = useMutation({
    mutationFn: () => situacaoApi.reativar(empresa.id, limpo),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['central'] });
      toast.success(`${empresa.nome} reativada`, {
        description: 'A mudança ficou registrada no histórico.',
      });
      onFeito();
    },
    onError: (e: any) =>
      toast.error(
        semRota(e)
          ? 'A reativação depende do backend desta etapa, que ainda não existe aqui. Nada foi gravado.'
          : (e?.response?.data?.detail ?? 'Não foi possível reativar a empresa'),
      ),
  });
  return (
    <Box
      mt="10px"
      p="12px 14px"
      borderRadius="14px"
      bg="rgba(220,252,231,.45)"
      boxShadow="inset 0 0 0 1px rgba(22,101,52,.18)"
    >
      <Text fontSize="13px" fontWeight={650} color="#166534">
        Reativar {empresa.nome}
      </Text>
      <Text fontSize="12.5px" color="#166534" mt="2px">
        {muda} Ela volta a aparecer em todas as abas e nas carteiras de quem já a tinha.
      </Text>
      <Textarea
        mt="10px"
        size="sm"
        borderRadius="10px"
        bg="white"
        rows={2}
        placeholder="Por que está reativando? (fica gravado no histórico)"
        aria-label="Motivo da reativação"
        value={motivo}
        onChange={e => setMotivo(e.target.value)}
      />
      <Flex justify="space-between" align="center" mt="8px" gap="8px" wrap="wrap">
        <Text fontSize="11.5px" color={TEXTO_SUAVE}>
          {limpo.length < 10
            ? `Motivo com pelo menos 10 letras (${limpo.length}/10)`
            : 'Pronto para gravar'}
        </Text>
        <Flex gap="6px">
          <Button size="sm" variant="ghost" borderRadius="999px" onClick={onFeito}>
            Cancelar
          </Button>
          <Button
            size="sm"
            borderRadius="999px"
            color="white"
            bg="linear-gradient(180deg, #16A34A, #15803D)"
            _hover={{ bg: '#15803D' }}
            leftIcon={<ArrowCounterClockwise size={14} />}
            isDisabled={limpo.length < 10}
            isLoading={reativar.isPending}
            onClick={() => reativar.mutate()}
          >
            Reativar e registrar
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
}

function Historico() {
  const {
    data = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['central', 'situacao-historico'],
    queryFn: () => situacaoApi.historico(),
    retry: false,
  });
  const ROTULO = { reativou: 'Reativou', inativou: 'Inativou', importacao: 'Importação' } as const;
  return (
    <Cartao p="18px 22px" display="flex" flexDirection="column" gap="10px">
      <Flex align="center" gap="8px">
        <ClockCounterClockwise size={16} />
        <Text fontSize="16px" fontWeight={650} color={TEXTO}>
          Histórico de situação
        </Text>
      </Flex>
      {isLoading && <Spinner size="sm" color={AZUL} />}
      {error && semRota(error) && (
        <Text
          fontSize="12.5px"
          color="#92400E"
          bg="rgba(254,243,199,.6)"
          p="10px 12px"
          borderRadius="12px"
        >
          Pré-visualização: o histórico entra com o backend desta etapa. Toda reativação, inativação
          e importação de planilha vai gravar quem fez, quando, o que era antes, o que ficou e o
          motivo.
        </Text>
      )}
      {error && !semRota(error) && (
        <Text fontSize="12.5px" color="#991B1B">
          Não foi possível carregar o histórico.
        </Text>
      )}
      {!isLoading && !error && data.length === 0 && (
        <Text fontSize="13px" color={TEXTO_SUAVE}>
          Nenhuma mudança de situação registrada.
        </Text>
      )}
      {data.map(h => (
        <Flex key={h.id} gap="10px" align="flex-start" fontSize="12.5px" wrap="wrap">
          <Text w="120px" flex="none" fontWeight={600} color={TEXTO}>
            {quandoFoi(h.dt_criacao)}
          </Text>
          <Etiqueta tom={h.acao === 'reativou' ? 'sucesso' : 'neutro'}>{ROTULO[h.acao]}</Etiqueta>
          <Box flex="1 1 240px" minW={0}>
            <Text color={TEXTO}>
              <strong>{h.empresa_nome}</strong> por {h.usuario_nome}
              {h.antes || h.depois ? ` · ${h.antes ?? '—'} → ${h.depois ?? '—'}` : ''}
            </Text>
            {h.motivo && <Text color={TEXTO_SUAVE}>“{h.motivo}”</Text>}
          </Box>
        </Flex>
      ))}
    </Cartao>
  );
}

/** Painel da direita no modo Empresas: as inativas, por motivo, para consultar e reativar. */
export function EmpresasInativasPainel() {
  const { isAdmin } = useAuth();
  const { inativas, porMotivo, ativas, carregando, rotaPropria } = useSituacaoDasEmpresas();
  const [motivo, setMotivo] = useState<MotivoInativa | 'todas'>('todas');
  const [busca, setBusca] = useState('');
  const [limite, setLimite] = useState(POR_PAGINA);
  const [reativando, setReativando] = useState<number | null>(null);

  const termo = busca.trim().toLowerCase();
  const digitos = termo.replace(/\D/g, '');
  const filtradas = inativas
    .filter(e => motivo === 'todas' || e.motivo === motivo)
    .filter(
      e =>
        !termo ||
        e.nome.toLowerCase().includes(termo) ||
        (digitos.length > 0 &&
          ((e.external_id ?? '') === digitos ||
            (e.cnpj ?? '').replace(/\D/g, '').includes(digitos))),
    )
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));

  return (
    <Flex direction="column" gap="16px">
      <Cartao p="20px 22px" display="flex" flexDirection="column" gap="14px">
        <Flex align="center" gap="14px" wrap="wrap">
          <Flex
            w="46px"
            h="46px"
            borderRadius="14px"
            align="center"
            justify="center"
            bg="#F1F5F9"
            color="#475569"
            flex="none"
          >
            <Prohibit size={24} weight="duotone" />
          </Flex>
          <Box flex="1 1 260px">
            <Eyebrow>EMPRESAS INATIVAS</Eyebrow>
            <Text fontSize="22px" lineHeight="28px" fontWeight={700} color={TEXTO}>
              {carregando ? '…' : `${inativas.length.toLocaleString('pt-BR')} inativas`}
            </Text>
            <Text fontSize="13px" color="#475569">
              Elas não aparecem nas outras abas da Central (carteiras, grupos de empresas, alocação)
              nem entram nas carteiras. Aqui é só para consultar e, se for o caso, reativar.
              {!carregando && ` ${ativas.toLocaleString('pt-BR')} empresas estão ativas.`}
            </Text>
          </Box>
        </Flex>
        <Flex gap="6px" wrap="wrap">
          <Pilula
            ativa={motivo === 'todas'}
            onClick={() => setMotivo('todas')}
            contagem={inativas.length}
          >
            Todas
          </Pilula>
          {(Object.keys(MOTIVOS) as MotivoInativa[])
            .filter(m => porMotivo[m])
            .map(m => (
              <Pilula
                key={m}
                ativa={motivo === m}
                onClick={() => setMotivo(m)}
                contagem={porMotivo[m]}
              >
                {MOTIVOS[m].rotulo}
              </Pilula>
            ))}
        </Flex>
        <Box maxW="420px">
          <Busca valor={busca} onChange={setBusca} placeholder="Buscar por nome, ID ou CNPJ" />
        </Box>
        {rotaPropria === false && (
          <Text fontSize="11.5px" color={TEXTO_SUAVE}>
            Contado a partir da lista de empresas (a rota própria de situação entra com o backend).
          </Text>
        )}
      </Cartao>

      <Cartao p={0} overflow="hidden">
        {carregando && (
          <Flex justify="center" py={10}>
            <Spinner color={AZUL} />
          </Flex>
        )}
        {!carregando && filtradas.length === 0 && (
          <Text p="20px" fontSize="13px" color={TEXTO_SUAVE}>
            Nenhuma empresa inativa com esse filtro.
          </Text>
        )}
        {filtradas.slice(0, limite).map(e => (
          <Box key={e.id} p="10px 20px" borderTop="1px solid rgba(15,23,42,.05)">
            <Flex align="center" gap="12px" wrap="wrap">
              <Buildings size={16} color={TEXTO_SUAVE} />
              <Box flex="1 1 240px" minW={0}>
                <Text fontSize="13.5px" fontWeight={600} color="#475569" noOfLines={1}>
                  {e.nome}
                </Text>
                <Text fontFamily={MONO} fontSize="11px" color={TEXTO_SUAVE}>
                  {identificacaoDaEmpresa(e)}
                  {e.grupo_de_empresas ? ` · ${e.grupo_de_empresas}` : ''}
                </Text>
              </Box>
              <Box flex="0 1 230px" minW={0}>
                <Etiqueta tom="neutro">{MOTIVOS[e.motivo].rotulo}</Etiqueta>
                <Text fontSize="11.5px" color={TEXTO_SUAVE} mt="2px" noOfLines={1}>
                  {detalheDoMotivo(e)}
                </Text>
              </Box>
              {isAdmin() && reativando !== e.id && (
                <Button
                  size="sm"
                  variant="outline"
                  bg="white"
                  borderRadius="999px"
                  color="#166534"
                  leftIcon={<ArrowCounterClockwise size={14} />}
                  onClick={() => setReativando(e.id)}
                >
                  Reativar
                </Button>
              )}
            </Flex>
            {reativando === e.id && <Reativar empresa={e} onFeito={() => setReativando(null)} />}
          </Box>
        ))}
        {filtradas.length > limite && (
          <Flex
            justify="space-between"
            align="center"
            p="12px 20px"
            borderTop="1px solid rgba(15,23,42,.06)"
            wrap="wrap"
            gap="8px"
          >
            <Text fontSize="12.5px" color={TEXTO_SUAVE}>
              Mostrando {limite} de {filtradas.length.toLocaleString('pt-BR')}
            </Text>
            <Button
              size="sm"
              variant="outline"
              bg="white"
              borderRadius="999px"
              onClick={() => setLimite(l => l + POR_PAGINA)}
            >
              Mostrar mais
            </Button>
          </Flex>
        )}
      </Cartao>

      <Historico />
    </Flex>
  );
}
